import { useEffect } from "react";
import { FormSection } from "../form-sections/FormSection";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { VehicleInformationSection } from "../form-sections/VehicleInformationSection";
import { DetailsSection } from "../form-sections/DetailsSection";
import { TireSection } from "../form-sections/TireSection";
import { EquipmentSection } from "../form-sections/EquipmentSection";
import { FinalDetailsSection } from "../form-sections/FinalDetailsSection";
import { Button } from "@/components/ui/button";
import {
  VehicleDetails,
  FormSections as FormSectionsType,
} from "@/types/vehicleDetails";
import { validateRegistrationFormatAny } from "@/utils/registrationValidation";

interface FormSectionsProps {
  vehicleDetails: Partial<VehicleDetails>;
  openSections: FormSectionsType;
  handleSelectChange: (
    value: string | boolean | number | string[],
    field: keyof VehicleDetails,
  ) => void;
  handleEditSection: (section: string, event: React.MouseEvent) => void;
  handleNext: (section: string) => void;
  onNext: () => void;
  showCondition?: boolean;
  isAutoLoaded?: boolean;
  onAutoLoadedFieldEditAttempt?: () => void;
  isManualMode?: boolean;
  regNumber?: string;
  onRegNumberChange?: (regNumber: string) => void;
}

export const FormSections = ({
  vehicleDetails,
  openSections,
  handleSelectChange,
  handleEditSection,
  handleNext,
  onNext,
  showCondition = true,
  isAutoLoaded = false,
  onAutoLoadedFieldEditAttempt,
  isManualMode = false,
  regNumber = "",
  onRegNumberChange,
}: FormSectionsProps) => {
  const { language } = useLanguage();
  const vLabels = valuationLabels(language).vehicleInfo;
  const conditionLabels = (vLabels as { conditions?: Record<string, string> })
    .conditions;
  const opts =
    (vLabels as { options?: Record<string, Record<string, string>> }).options ??
    {};
  const getConditionDisplay = (c: string | undefined) =>
    (conditionLabels && c && conditionLabels[c]) || c || "";
  const getOptionLabel = (
    category:
      | "fuelType"
      | "bodyType"
      | "transmission"
      | "color"
      | "tireType"
      | "rimType",
    svKey: string,
  ) => opts[category]?.[svKey] ?? svKey;
  const isFinalSectionComplete =
    vehicleDetails.condition && vehicleDetails.mileage;

  return (
    <>
      <FormSection
        isOpen={openSections.makeModel}
        title={vLabels.title}
        value={`${vehicleDetails.make || ""} ${vehicleDetails.model || ""} ${
          vehicleDetails.year || ""
        }`}
        onEdit={(e) => handleEditSection("makeModel", e)}
        isCompleted={(() => {
          // In manual mode, require regNumber with valid format before marking as complete
          if (isManualMode) {
            if (!regNumber || !validateRegistrationFormatAny(regNumber)) {
              return false;
            }
          }
          return (
            !!vehicleDetails.make &&
            !!vehicleDetails.model &&
            !!vehicleDetails.year
          );
        })()}
      >
        <VehicleInformationSection
          make={vehicleDetails.make}
          model={vehicleDetails.model}
          variant={vehicleDetails.variant}
          year={vehicleDetails.year}
          onMakeSelect={(make) => handleSelectChange(make, "make")}
          onModelSelect={(model) => handleSelectChange(model, "model")}
          onVariantChange={(variant) => handleSelectChange(variant, "variant")}
          onYearSelect={(year) => handleSelectChange(year, "year")}
          onVehicleTypeSelect={(vehicleType) =>
            handleSelectChange(vehicleType, "vehicleType")
          }
          isAutoLoaded={isAutoLoaded}
          onAutoLoadedFieldEditAttempt={onAutoLoadedFieldEditAttempt}
          isManualMode={isManualMode}
          regNumber={regNumber}
          onRegNumberChange={onRegNumberChange}
        />
        {/* Show "Nästa" button when Fordonsinfo is complete - for both manual mode and valuation */}
        {(() => {
          const isValidRegFormat = regNumber
            ? validateRegistrationFormatAny(regNumber)
            : false;
          const isComplete =
            !!vehicleDetails.make &&
            !!vehicleDetails.model &&
            !!vehicleDetails.year &&
            (!isManualMode || (!!regNumber && isValidRegFormat));
          return isComplete ? (
            <div className="mt-4">
              <Button
                className="w-full"
                onClick={() => handleNext("makeModel")}
                disabled={!isComplete}
              >
                {vLabels.next}
              </Button>
            </div>
          ) : null;
        })()}
      </FormSection>

      {!!vehicleDetails.make &&
        !!vehicleDetails.model &&
        !!vehicleDetails.year && (
          <FormSection
            isOpen={openSections.details}
            title={vLabels.moreDetails}
            value={(() => {
              const isElectric =
                vehicleDetails.fuelType === "El" ||
                vehicleDetails.fuelType === "Elbil" ||
                vehicleDetails.fuelType === "ELECTRIC";

              const parts: string[] = [];
              if (vehicleDetails.fuelType) {
                parts.push(getOptionLabel("fuelType", vehicleDetails.fuelType));
              }
              // Only show engine for non-electric vehicles (engine names not translated)
              if (!isElectric && vehicleDetails.engine) {
                parts.push(vehicleDetails.engine);
              }
              if (vehicleDetails.bodyType)
                parts.push(getOptionLabel("bodyType", vehicleDetails.bodyType));
              if (vehicleDetails.color)
                parts.push(getOptionLabel("color", vehicleDetails.color));
              if (vehicleDetails.transmission)
                parts.push(
                  getOptionLabel("transmission", vehicleDetails.transmission),
                );
              if (vehicleDetails.variant)
                parts.push(
                  vehicleDetails.variant.length > 15
                    ? vehicleDetails.variant.substring(0, 10) + "..."
                    : vehicleDetails.variant,
                );
              return parts.join(" | ");
            })()}
            onEdit={(e) => handleEditSection("details", e)}
          >
            <DetailsSection
              fuelType={vehicleDetails.fuelType}
              engine={vehicleDetails.engine}
              transmission={vehicleDetails.transmission}
              bodyType={vehicleDetails.bodyType}
              color={vehicleDetails.color}
              variant={vehicleDetails.variant}
              onSelect={handleSelectChange}
              onVariantChange={(variant) =>
                handleSelectChange(variant, "variant")
              }
              trim={vehicleDetails.trim}
              onTrimChange={(trim) => handleSelectChange(trim, "trim")}
              selectedMake={vehicleDetails.make}
              isAutoLoaded={isAutoLoaded}
              vehicleType={vehicleDetails.vehicleType}
              onAutoLoadedFieldEditAttempt={onAutoLoadedFieldEditAttempt}
            />
            {/* Show "Nästa" button when all required fields are filled */}
            {(() => {
              const isElectric =
                vehicleDetails.fuelType === "El" ||
                vehicleDetails.fuelType === "Elbil" ||
                vehicleDetails.fuelType === "ELECTRIC";
              const hasEngine = isElectric ? true : !!vehicleDetails.engine;
              const isComplete =
                !!vehicleDetails.fuelType &&
                hasEngine &&
                (vehicleDetails.vehicleType === "mc" ||
                  !!vehicleDetails.bodyType) &&
                !!vehicleDetails.transmission &&
                !!vehicleDetails.color;

              return (
                isComplete && (
                  <div className="mt-6">
                    <Button
                      className="w-full"
                      onClick={() => handleNext("details")}
                    >
                      {vLabels.next}
                    </Button>
                  </div>
                )
              );
            })()}
          </FormSection>
        )}

      {(() => {
        const isElectric =
          vehicleDetails.fuelType === "El" ||
          vehicleDetails.fuelType === "Elbil" ||
          vehicleDetails.fuelType === "ELECTRIC";
        const hasEngine = isElectric ? true : !!vehicleDetails.engine;
        return (
          !!vehicleDetails.fuelType &&
          hasEngine &&
          (vehicleDetails.vehicleType === "mc" || !!vehicleDetails.bodyType) &&
          !!vehicleDetails.transmission &&
          !!vehicleDetails.color
        );
      })() && (
        <FormSection
          isOpen={openSections.equipment}
          title={vLabels.equipment}
          value={`${vehicleDetails.equipment?.length || 0} ${vLabels.selectedCount}`}
          onEdit={(e) => handleEditSection("equipment", e)}
          isCompleted={
            !!vehicleDetails.equipment && vehicleDetails.equipment.length > 0
          }
        >
          <div className="space-y-4">
            <EquipmentSection
              equipment={vehicleDetails.equipment || []}
              vehicleType={vehicleDetails.vehicleType}
              onEquipmentSelect={(equipment) => {
                // Update equipment as a complete array
                handleSelectChange(equipment, "equipment");
              }}
            />
            {vehicleDetails.equipment &&
              vehicleDetails.equipment.length > 0 && (
                <Button
                  className="w-full"
                  onClick={() => handleNext("equipment")}
                >
                  {vLabels.next}
                </Button>
              )}
          </div>
        </FormSection>
      )}

      {(() => {
        const isElectric =
          vehicleDetails.fuelType === "El" ||
          vehicleDetails.fuelType === "Elbil" ||
          vehicleDetails.fuelType === "ELECTRIC";
        const hasEngine = isElectric ? true : !!vehicleDetails.engine;
        return (
          !!vehicleDetails.fuelType &&
          hasEngine &&
          (vehicleDetails.vehicleType === "mc" || !!vehicleDetails.bodyType) &&
          !!vehicleDetails.transmission &&
          !!vehicleDetails.color &&
          vehicleDetails.equipment &&
          vehicleDetails.equipment.length > 0
        );
      })() && (
        <FormSection
          isOpen={openSections.tires}
          title={vLabels.tires}
          value={(() => {
            const parts: string[] = [];
            if (vehicleDetails.currentTireType)
              parts.push(
                getOptionLabel("tireType", vehicleDetails.currentTireType),
              );
            if (vehicleDetails.currentRimType)
              parts.push(
                getOptionLabel("rimType", vehicleDetails.currentRimType),
              );
            if (vehicleDetails.extraTireSet) parts.push(vLabels.extraSet);
            return parts.join(" | ");
          })()}
          onEdit={(e) => handleEditSection("tires", e)}
          isCompleted={(() => {
            if (
              !vehicleDetails.currentTireType ||
              !vehicleDetails.currentRimType
            ) {
              return false;
            }
            // If extraTireSet is true, require extraTireType and extraRimType
            if (vehicleDetails.extraTireSet === true) {
              if (
                !vehicleDetails.extraTireType ||
                !vehicleDetails.extraRimType
              ) {
                return false;
              }
            }
            return true;
          })()}
        >
          <div className="space-y-4">
            <TireSection
              currentTireType={vehicleDetails.currentTireType}
              currentRimType={vehicleDetails.currentRimType}
              extraTireSet={vehicleDetails.extraTireSet}
              extraTireType={vehicleDetails.extraTireType}
              extraRimType={vehicleDetails.extraRimType}
              onSelect={handleSelectChange}
            />
            {(() => {
              const hasCurrentTires =
                !!vehicleDetails.currentTireType &&
                !!vehicleDetails.currentRimType;
              const hasExtraTires =
                vehicleDetails.extraTireSet === true
                  ? !!vehicleDetails.extraTireType &&
                    !!vehicleDetails.extraRimType
                  : true; // If extraTireSet is false or undefined, no extra tires needed
              return hasCurrentTires && hasExtraTires;
            })() && (
              <Button className="w-full" onClick={() => handleNext("tires")}>
                {vLabels.next}
              </Button>
            )}
          </div>
        </FormSection>
      )}

      {(() => {
        const isElectric =
          vehicleDetails.fuelType === "El" ||
          vehicleDetails.fuelType === "Elbil" ||
          vehicleDetails.fuelType === "ELECTRIC";
        const hasEngine = isElectric ? true : !!vehicleDetails.engine;
        const tiresCompleted =
          !!vehicleDetails.currentTireType && !!vehicleDetails.currentRimType;
        return (
          !!vehicleDetails.fuelType &&
          hasEngine &&
          (vehicleDetails.vehicleType === "mc" || !!vehicleDetails.bodyType) &&
          !!vehicleDetails.transmission &&
          !!vehicleDetails.color &&
          vehicleDetails.equipment &&
          vehicleDetails.equipment.length > 0 &&
          tiresCompleted
        );
      })() && (
        <FormSection
          isOpen={openSections.final}
          title={vLabels.finalDetails}
          value={`${
            showCondition && vehicleDetails.condition
              ? `${getConditionDisplay(vehicleDetails.condition)} | `
              : ""
          }${vehicleDetails.mileage || ""} ${vLabels.milesUnit ?? "mil"} | ${
            vehicleDetails.keys
              ? (vehicleDetails.keys === 3 ? "3+" : vehicleDetails.keys) +
                ` ${vLabels.keys}`
              : ""
          }
          ${vehicleDetails.horsepower ? ` | ${vehicleDetails.horsepower} ${vLabels.hp}` : ""}
          ${vehicleDetails.fuelType === "El" || vehicleDetails.fuelType === "Elbil" || vehicleDetails.fuelType === "ELECTRIC" ? ` | ${vehicleDetails.electricDriveRange} km` : ""}
          `}
          onEdit={(e) => handleEditSection("final", e)}
          isCompleted={
            !!vehicleDetails.condition &&
            !!vehicleDetails.mileage &&
            !!vehicleDetails.keys
          }
        >
          <div className="space-y-4">
            <FinalDetailsSection
              mileage={vehicleDetails.mileage?.toString()}
              condition={vehicleDetails.condition}
              keys={vehicleDetails.keys}
              horsePower={vehicleDetails.horsepower}
              electricDriveRange={vehicleDetails.electricDriveRange}
              fuelType={vehicleDetails.fuelType}
              onMileageChange={(mileage) =>
                handleSelectChange(mileage, "mileage")
              }
              onConditionChange={(condition) =>
                handleSelectChange(condition, "condition")
              }
              onKeysChange={(keys) => handleSelectChange(keys, "keys")}
              onHorsePowerChange={(horsePower) =>
                handleSelectChange(horsePower, "horsepower")
              }
              onElectricDriveRangeChange={(electricDriveRange) =>
                handleSelectChange(electricDriveRange, "electricDriveRange")
              }
              onNext={onNext}
              showCondition={showCondition}
            />
          </div>
        </FormSection>
      )}
    </>
  );
};
